import React, { useEffect, useState } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Clock } from "lucide-react";
import { resolveIOTFCutOff, type IOTFLookupResult } from "@/lib/pharmacyFirst/engine/iotfLookup";
import type { IOTFLookupNode as IOTFLookupNodeType } from "@/lib/pharmacyFirst/types";
import type { IOTFCutOffTable } from "@/lib/pharmacyFirst/types/iotf.types";
import type { BiologicalSex } from "@/lib/pharmacyFirst/types/patient.types";

export interface IOTFLookupAnswer {
  result: IOTFLookupResult;
  ageMonths: number;
  biologicalSex: BiologicalSex;
  bmi: number;
  cutOffUsed: number | null;
}

interface IOTFLookupNodeProps {
  node: IOTFLookupNodeType;
  ageMonths: number;
  biologicalSex: BiologicalSex;
  bmi: number;
  iotfTable: IOTFCutOffTable;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: IOTFLookupAnswer) => void;
}

export function IOTFLookupNode({
  node,
  ageMonths,
  biologicalSex,
  bmi,
  iotfTable,
  isDarkMode = false,
  stepNumber,
  onAnswer,
}: IOTFLookupNodeProps) {
  const [result, setResult] = useState<IOTFLookupResult | null>(null);

  useEffect(() => {
    const lookupResult = resolveIOTFCutOff(ageMonths, biologicalSex, bmi, iotfTable);
    setResult(lookupResult);

    if (
      lookupResult === "meets_threshold" ||
      lookupResult === "below_threshold" ||
      lookupResult === "no_entry_found"
    ) {
      const cutOffEntry =
        biologicalSex !== "other"
          ? [...iotfTable.cutOffs]
            .filter((c) => c.sex === biologicalSex)
            .sort((a, b) => b.ageMonths - a.ageMonths)
            .find((c) => c.ageMonths <= ageMonths)
          : null;

      onAnswer({
        result: lookupResult,
        ageMonths,
        biologicalSex,
        bmi,
        cutOffUsed: cutOffEntry?.bmiCutOff ?? null,
      });
    }
  }, []);

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  if (result === null) {
    return (
      <div className={cardClass}>
        <div className="flex items-center gap-3">
          {stepNumber !== undefined && (
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
            >
              {stepNumber}
            </div>
          )}
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Checking IOTF BMI threshold…
          </p>
        </div>
      </div>
    );
  }

  if (result === "pending_data") {
    return (
      <div className={cardClass}>
        <div className="flex items-center gap-3 mb-4">
          {stepNumber !== undefined && (
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
            >
              {stepNumber}
            </div>
          )}
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            IOTF BMI threshold check
          </h3>
        </div>

        <Alert className={`mb-4 ${isDarkMode ? "border-amber-700 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
          <Clock className={`h-4 w-4 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
          <AlertDescription className={isDarkMode ? "text-amber-200" : "text-amber-900"}>
            The IOTF adolescent BMI cut-off data has not yet been received.
            This pathway section is not yet operational. The patient will be
            routed to the appropriate blocked outcome.
          </AlertDescription>
        </Alert>

        <FlowPrimaryButton
          label="Continue"
          className="w-full"
          onClick={() =>
            onAnswer({
              result: "pending_data",
              ageMonths,
              biologicalSex,
              bmi,
              cutOffUsed: null,
            })
          }
        />
      </div>
    );
  }

  if (result === "sex_not_resolvable") {
    return (
      <div className={cardClass}>
        <div className="flex items-center gap-3 mb-4">
          {stepNumber !== undefined && (
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
            >
              {stepNumber}
            </div>
          )}
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            IOTF BMI threshold check
          </h3>
        </div>

        <Alert className={`mb-4 ${isDarkMode ? "border-amber-700 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
          <AlertCircle className={`h-4 w-4 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
          <AlertDescription className={isDarkMode ? "text-amber-200" : "text-amber-900"}>
            The IOTF cut-off table provides thresholds for male and female
            patients only. For this patient, the threshold cannot be determined
            automatically. The patient will be referred to GP for specialist
            assessment.
          </AlertDescription>
        </Alert>

        <FlowPrimaryButton
          label="Continue — refer to GP"
          className="w-full"
          onClick={() =>
            onAnswer({
              result: "sex_not_resolvable",
              ageMonths,
              biologicalSex,
              bmi,
              cutOffUsed: null,
            })
          }
        />
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          Processing…
        </p>
      </div>
    </div>
  );
}
